import HeaderV3Desktop from './header-v3-desktop';
import HeaderV3Mobile from './header-v3-mobile';

const REGION_COOKIE = 'cg_region';
const REGIONS = ['eu', 'global'];

// Root of the v3 header: scroll behaviour, the region preference, and the
// desktop/mobile sub-components. Mounted on [data-header-v3].
//
// Scroll (headroom): after 8px the bar gets a translucent background; past
// 120px it slides away on scroll-down and back on scroll-up. It never hides
// while anything is open (dropdown, popover, drawer, sheet) — the
// sub-components call setOpen() so this class can tell.
export default class HeaderV3 {
    constructor(element) {
        this.root = element;
        this.openCount = 0;
        this.lastY = window.scrollY;
        this.ticking = false;

        this.initRegion();

        const desktop = this.root.querySelector('[data-hv3-desktop]');
        const mobile = this.root.querySelector('[data-hv3-mobile]');
        if (desktop) new HeaderV3Desktop(desktop, this);
        if (mobile) new HeaderV3Mobile(mobile, this);

        window.addEventListener('scroll', () => this.requestScroll(), { passive: true });
        this.onScroll();
    }

    // Called by sub-components whenever something opens or closes. The header
    // stays put while anything is open.
    setOpen(open) {
        this.openCount = Math.max(0, this.openCount + (open ? 1 : -1));
        this.root.classList.toggle('is-open', this.openCount > 0);
        if (this.openCount > 0) {
            this.root.classList.remove('is-hidden');
        }
    }

    requestScroll() {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
            this.ticking = false;
            this.onScroll();
        });
    }

    onScroll() {
        const y = window.scrollY;
        this.root.classList.toggle('is-scrolled', y > 8);

        if (this.openCount === 0) {
            if (y > this.lastY + 4 && y > 120) {
                this.root.classList.add('is-hidden');
            } else if (y < this.lastY - 4 || y <= 120) {
                this.root.classList.remove('is-hidden');
            }
        }

        this.lastY = y;
    }

    // ---- Region preference -------------------------------------------------
    // Cosmetic for now: remembered in a cookie, reflected in the picker labels.
    // The server always renders the default (pages are cached without varying
    // on the cookie), so the choice is applied here on load.

    initRegion() {
        this.applyRegion(this.readRegion());

        this.root.addEventListener('click', (event) => {
            const option = event.target.closest('[data-region-option]');
            if (!option || !this.root.contains(option)) return;
            this.applyRegion(option.dataset.regionOption, true);
            this.root.dispatchEvent(new CustomEvent('hv3:region-change', { bubbles: false }));
        });
    }

    readRegion() {
        const match = document.cookie.match(new RegExp('(?:^|; )' + REGION_COOKIE + '=([^;]*)'));
        const value = match ? decodeURIComponent(match[1]) : '';
        return REGIONS.includes(value) ? value : 'eu';
    }

    applyRegion(region, persist = false) {
        if (!REGIONS.includes(region)) return;

        this.root.dataset.region = region;

        this.root.querySelectorAll('[data-hv3-region-label]').forEach((label) => {
            const text = label.getAttribute('data-label-' + region);
            if (text !== null) label.textContent = text;
        });

        this.root.querySelectorAll('[data-region-option]').forEach((option) => {
            option.setAttribute('aria-checked', option.dataset.regionOption === region ? 'true' : 'false');
        });

        if (persist) {
            const secure = window.location.protocol === 'https:' ? '; Secure' : '';
            document.cookie = REGION_COOKIE + '=' + region + '; Max-Age=31536000; Path=/; SameSite=Lax' + secure;
        }
    }
}
